import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, ShieldCheck, Loader2 } from "lucide-react";

const emptyLine = { name: "", hsn: "", qty: 0, unit: "Pack", rate: 0, tax_percent: 0, category: "" };

/**
 * Manually enter a legacy invoice for an existing portal-enabled client so
 * their dashboard shows real history at pilot launch — used for backfilling
 * Rulen Hospitality and Cambrian Hall's last 3+ orders (P5).
 *
 * Endpoint: POST /api/clients/{client_id}/historical-invoice
 */
export default function HistoricalInvoiceModal({ client, onClose, onSaved }) {
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [invoiceDate, setInvoiceDate] = useState("");
    const [invoiceType, setInvoiceType] = useState("B2B");
    const [notes, setNotes] = useState("");
    const [lines, setLines] = useState([{ ...emptyLine }]);
    const [busy, setBusy] = useState(false);

    const addLine = () => setLines([...lines, { ...emptyLine }]);
    const removeLine = (i) => setLines(lines.filter((_, j) => j !== i));
    const updateLine = (i, patch) =>
        setLines(lines.map((l, j) => j === i ? { ...l, ...patch } : l));

    const totals = lines.reduce((acc, l) => {
        const lt = (l.qty || 0) * (l.rate || 0);
        const tx = lt * (l.tax_percent || 0) / 100;
        acc.subtotal += lt;
        acc.tax += tx;
        return acc;
    }, { subtotal: 0, tax: 0 });

    const save = async () => {
        if (!invoiceDate) return toast.error("Invoice date is required");
        const valid = lines.filter(l => l.name && l.qty > 0);
        if (valid.length === 0) return toast.error("At least one line item with a name and qty > 0 is required");
        setBusy(true);
        try {
            await api.post(`/clients/${client.id}/historical-invoice`, {
                invoice_number: invoiceNumber || "",
                invoice_date: invoiceDate,
                invoice_type: invoiceType,
                notes,
                line_items: valid.map(l => ({
                    name: l.name, hsn: l.hsn || "", qty: parseFloat(l.qty) || 0,
                    unit: l.unit || "Pack", rate: parseFloat(l.rate) || 0,
                    tax_percent: parseFloat(l.tax_percent) || 0,
                    category: l.category || "Uncategorised",
                })),
            });
            toast.success(`Historical invoice added for ${client.name}`);
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
             data-testid="historical-invoice-modal" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 my-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4 gap-3">
                    <div>
                        <h3 className="text-xl font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Add historical invoice
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Backfilling past orders for <b>{client.name}</b> so their portal dashboard shows real history from day one.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <label className="block">
                        <div className="hdb-label">Invoice # (optional)</div>
                        <input className="hdb-input hdb-mono" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)}
                               placeholder="Auto-generated if blank" data-testid="hist-invoice-number" />
                    </label>
                    <label className="block">
                        <div className="hdb-label">Invoice date *</div>
                        <input type="date" className="hdb-input hdb-mono" value={invoiceDate}
                               onChange={(e) => setInvoiceDate(e.target.value)}
                               data-testid="hist-invoice-date" />
                    </label>
                    <label className="block">
                        <div className="hdb-label">Type</div>
                        <select className="hdb-input" value={invoiceType} onChange={(e) => setInvoiceType(e.target.value)}
                                data-testid="hist-invoice-type">
                            <option value="B2B">B2B</option>
                            <option value="B2C">B2C</option>
                        </select>
                    </label>
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-3 py-2">Item name *</th>
                                <th className="text-left px-3 py-2">Category</th>
                                <th className="text-left px-3 py-2">HSN</th>
                                <th className="text-right px-3 py-2">Qty *</th>
                                <th className="text-left px-3 py-2">Unit</th>
                                <th className="text-right px-3 py-2">Rate</th>
                                <th className="text-right px-3 py-2">Tax %</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {lines.map((l, i) => (
                                <tr key={i} data-testid={`hist-line-${i}`}>
                                    <td><input className="w-full px-2 py-1 border-0 text-sm" value={l.name}
                                               onChange={(e) => updateLine(i, { name: e.target.value })}
                                               placeholder="Basmati Rice 5 kg" data-testid={`hist-line-${i}-name`} /></td>
                                    <td><input className="w-full px-2 py-1 border-0 text-sm" value={l.category}
                                               onChange={(e) => updateLine(i, { category: e.target.value })}
                                               placeholder="Rice, Flour…" /></td>
                                    <td><input className="w-24 px-2 py-1 border-0 text-sm font-mono" value={l.hsn}
                                               onChange={(e) => updateLine(i, { hsn: e.target.value })} /></td>
                                    <td className="text-right"><input type="number" min="0" step="0.5"
                                               className="w-20 px-2 py-1 border-0 text-sm font-mono text-right" value={l.qty || ""}
                                               onChange={(e) => updateLine(i, { qty: parseFloat(e.target.value) || 0 })}
                                               data-testid={`hist-line-${i}-qty`} /></td>
                                    <td><input className="w-16 px-2 py-1 border-0 text-sm" value={l.unit}
                                               onChange={(e) => updateLine(i, { unit: e.target.value })} /></td>
                                    <td className="text-right"><input type="number" min="0" step="0.01"
                                               className="w-24 px-2 py-1 border-0 text-sm font-mono text-right" value={l.rate || ""}
                                               onChange={(e) => updateLine(i, { rate: parseFloat(e.target.value) || 0 })}
                                               data-testid={`hist-line-${i}-rate`} /></td>
                                    <td className="text-right"><input type="number" min="0" step="0.5"
                                               className="w-16 px-2 py-1 border-0 text-sm font-mono text-right" value={l.tax_percent || ""}
                                               onChange={(e) => updateLine(i, { tax_percent: parseFloat(e.target.value) || 0 })} /></td>
                                    <td className="text-right pr-2">
                                        <button onClick={() => removeLine(i)} disabled={lines.length === 1}
                                                className="text-slate-400 hover:text-red-600 disabled:opacity-30 p-1">
                                            <Trash2 size={13}/>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="p-2 border-t border-slate-100 bg-slate-50">
                        <button onClick={addLine} className="hdb-btn hdb-btn-ghost text-xs" data-testid="hist-add-line">
                            <Plus size={11}/> Add line
                        </button>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="block">
                        <div className="hdb-label">Notes (optional)</div>
                        <textarea rows={2} className="hdb-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                                  placeholder="Any context — from paper invoice, source, etc." />
                    </label>
                    <div className="bg-slate-900 text-white rounded-xl p-4">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Computed totals</div>
                        <div className="flex justify-between text-sm mt-1">
                            <span className="text-slate-400">Subtotal</span>
                            <span className="font-mono">₹ {totals.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Tax</span>
                            <span className="font-mono">₹ {totals.tax.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-black border-t border-slate-700 pt-1 mt-1">
                            <span>Grand total</span>
                            <span className="font-mono">₹ {(totals.subtotal + totals.tax).toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="hdb-btn hdb-btn-ghost">Cancel</button>
                    <button onClick={save} disabled={busy} className="hdb-btn hdb-btn-primary" data-testid="hist-save-btn">
                        {busy ? <><Loader2 size={13} className="animate-spin"/> Saving…</>
                              : <><ShieldCheck size={13}/> Save historical invoice</>}
                    </button>
                </div>
            </div>
        </div>
    );
}
